/* eslint-disable no-shadow */
import React, { useEffect, useState } from 'react';
import { COLOR } from '@src/styles/color';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import CommonButton from '@src/new-components/Button';
import apis from '@src/apis';
import actions from '@src/redux/actions';
import {
  USERNAME_EXISTS,
  VOICE_CLONE_TYPE,
  VOICE_LOCALES,
  VOICE_NAME_REGEX,
  VOICE_PROCESSING_INTERVAL,
  VOICE_PROCESSING_STATUS,
} from '@src/constants/voiceCloning';
import sanitizeHtml from 'sanitize-html';
import {
  Typography,
  Box,
  Grid,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from '@mui/material';
import { v4 as uuid } from 'uuid';
import { uploadFileToS3 } from '@src/services/upload';

import { GENDER } from '@src/constants/voice';
import { getCategoryLabel } from '@src/services/voiceCloning';
import LimitedInput from '@src/new-components/Input/LimitedInput';
import Select from '@src/new-components/Select';
import { removeWhiteSpace, unsignText } from '@src/utils/string';
import { ERROR_CODE } from '@src/errors/code';
import useAgeGroupVoiceCloning from '@src/hooks/useAgeGroupVoiceCloning';

import { STEPS } from '@src/constants/instantVoiceCloning';
import { sortProvinces } from '@src/utils/province';
import VoiceProcessing from './VoiceProcessing';
import VoiceProcessingError from './VoiceProcessingError';
import AudioPlay from './AudioPlay';
import AvatarInput from './AvatarInput';
import { StyledCreateVoice } from './index.style';

const MAX_NAME_LENGTH = 25;
const MIN_NAME_LENGTH = 5;

const CreateVoice = ({
  audioDraft,
  setAudioDraft,
  closePopupInstantVoiceCloning,
  refreshVoices,
  onSetStep,
  onResetMetadataAudio,
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { provinces: provincesStore } = useSelector((state) => state.province);
  const { instantVoiceTraining } =
    useSelector((state) => state.instantVoiceTraining) || {};

  const [isLoadingCreateVoice, setIsLoadingCreateVoice] = useState(false);
  const [isLoadingTryAgain, setIsLoadingTryAgain] = useState(false);
  const [progress, setProgress] = useState(0);
  const [voiceProcessingStatus, setVoiceProcessingStatus] = useState(
    VOICE_PROCESSING_STATUS.PROCESSING,
  );
  const [agreed, setAgreed] = useState(false);
  const [provinces, setProvinces] = useState(provincesStore || []);
  const [categories, setCategories] = useState([]);

  const [avatar, setAvatar] = useState({
    file: null,
    link: null,
  });
  const [audio, setAudio] = useState({
    originalAudioLink: null,
    cloneAudioLink: null,
  });

  const [voiceInfo, setVoiceInfo] = useState({
    voiceName: '',
    region: '',
    province: '',
    gender: '',
    contentType: '',
  });

  const [error, setError] = useState({
    voiceName: false,
    gender: false,
    region: false,
    province: false,
    contentType: false,
  });

  const { ageGroupOptions } = useAgeGroupVoiceCloning();

  useEffect(() => {
    setVoiceInfo({ ...voiceInfo, ageGroup: '' });
    setError({ ...error, ageGroup: false });
  }, []);

  const fetchProvinces = async () => {
    const response = await apis.voiceCloning.getVoiceCloningProvinces({
      searchFields: 'name,code,locale',
    });
    if (!response?.status) {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: 'getProvincesFailed',
        }),
      );
      return;
    }
    const sortedProvinces = sortProvinces(response?.result?.provinces);

    dispatch(actions.province.addProvinces(sortedProvinces));
  };

  const fetchCategories = async () => {
    const response = await apis.voiceCloning.getVoiceCloningCategories();

    if (!response?.status) {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: 'getCategoriesFailed',
        }),
      );

      return;
    }
    const categoriesArray = response?.result?.categories || [];
    setCategories(categoriesArray);
    // TODO: dispatch add categories
  };

  const fetchTrainingZeroShotProgress = async () => {
    const response = await apis.voiceCloning.getTrainingZeroShotProgress(
      audioDraft?.voiceId,
    );

    if (response && response?.status) {
      const { voiceId, status, sampleLink } = response.result;
      dispatch(
        actions.instantVoiceTraining.addInstantVoiceTraining({
          voiceId,
          status,
          sampleLink,
        }),
      );
    }
  };

  // Polling to get the progress of the voice cloning
  useEffect(() => {
    const fetchTrainingZeroShotProgressInterval = setInterval(async () => {
      if (
        audioDraft?.voiceId &&
        voiceProcessingStatus === VOICE_PROCESSING_STATUS.PROCESSING
      ) {
        await fetchTrainingZeroShotProgress();
      }
    }, VOICE_PROCESSING_INTERVAL);
    return () => clearInterval(fetchTrainingZeroShotProgressInterval);
  }, [audioDraft?.voiceId, voiceProcessingStatus]);

  useEffect(() => {
    if (!provincesStore.length) fetchProvinces();
    fetchCategories();
  }, []);

  const handleSelect = (e) => {
    if (e.target.name === 'voiceName') {
      if (e.target.value.length > MAX_NAME_LENGTH) {
        const value = e.target.value.slice(0, MAX_NAME_LENGTH);
        setVoiceInfo({ ...voiceInfo, voiceName: value });
        setError({ ...error, voiceName: false });
        return;
      }
    }
    setVoiceInfo({ ...voiceInfo, [e.target.name]: e.target.value });
    setError({ ...error, [e.target.name]: false });
  };

  useEffect(() => {
    if (voiceInfo.region) setVoiceInfo({ ...voiceInfo, province: '' });
  }, [voiceInfo.region]);

  useEffect(() => {
    if (!voiceInfo.region) {
      setProvinces(provincesStore);
      return;
    }
    const filteredProvincesByLocale = provincesStore.filter((item) =>
      item.locale.toLowerCase().includes(voiceInfo.region.toLowerCase()),
    );
    setProvinces(filteredProvincesByLocale);
  }, [voiceInfo.region, provincesStore]);

  useEffect(() => {
    const UPDATE_INTERVAL = 100;
    const interval = setInterval(() => {
      setProgress((prevProgress) => {
        // Slow down progress as it approaches 100 to give time for WebSocket response
        const increment = prevProgress > 90 ? 0.1 : Math.random() * 2;
        return Math.min(prevProgress + increment, 99); // Cap at 95% until WebSocket confirms completion
      });
    }, UPDATE_INTERVAL);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (
      voiceProcessingStatus === VOICE_PROCESSING_STATUS.PROCESSING &&
      instantVoiceTraining?.status === VOICE_PROCESSING_STATUS.SUCCESS &&
      instantVoiceTraining?.voiceId === audioDraft?.voiceId
    ) {
      setProgress(100);
      setVoiceProcessingStatus(VOICE_PROCESSING_STATUS.SUCCESS);
      setAudio({
        originalAudioLink: audioDraft?.audioLink,
        cloneAudioLink: instantVoiceTraining?.sampleLink,
      });
    }
    if (
      voiceProcessingStatus === VOICE_PROCESSING_STATUS.PROCESSING &&
      instantVoiceTraining?.status === VOICE_PROCESSING_STATUS.FAILURE &&
      instantVoiceTraining?.voiceId === audioDraft?.voiceId
    ) {
      setVoiceProcessingStatus(VOICE_PROCESSING_STATUS.FAILURE);
    }
  }, [instantVoiceTraining?.status, instantVoiceTraining?.voiceId]);

  const disabledCreateVoice =
    voiceProcessingStatus !== VOICE_PROCESSING_STATUS.SUCCESS ||
    isLoadingCreateVoice ||
    !agreed;

  const handleUploadAvatarToS3 = async (fileName, file) => {
    const extension = fileName.split('.')[1];
    const name = fileName.split('.')[0];
    const directory = 'images/voice-cloning';
    const s3Url = await uploadFileToS3(name, directory, extension, file);

    if (!s3Url) {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: 'uploadError',
        }),
      );
      return null;
    }

    return s3Url;
  };

  const handleCreateVoice = async () => {
    const newError = {};
    setIsLoadingCreateVoice(true);
    Object.keys(voiceInfo).forEach((key) => {
      if (!voiceInfo[key]) {
        newError[key] = t('requiredField', { field: t(key) });
      }
    });
    if (voiceInfo.voiceName) {
      // Normalize the voice name to NFC to avoid issues with Vietnamese characters
      const normalizedVoiceName = voiceInfo.voiceName.normalize('NFC');
      if (normalizedVoiceName.length < MIN_NAME_LENGTH)
        newError.voiceName = t('voiceNameDescription');
      const isValidCharacters = VOICE_NAME_REGEX.test(normalizedVoiceName);
      if (!isValidCharacters) newError.voiceName = t('voiceNameDescription');
    }
    if (Object.values(newError).some((value) => value)) {
      setError(newError);
      setIsLoadingCreateVoice(false);
      return;
    }

    if (avatar.file) {
      const avatarName = avatar.file.name;
      const ext = avatarName.split('.').pop();
      const imgName = avatarName.slice(0, avatarName.lastIndexOf('.'));
      const link = await handleUploadAvatarToS3(
        `${imgName}_${uuid()}.${ext}`,
        avatar.file,
      );
      voiceInfo.avatar = link || undefined;
    }

    let username = voiceInfo.voiceName.toLowerCase();
    username = unsignText(username);
    username = removeWhiteSpace(username);

    const data = {
      categoryId: voiceInfo.contentType,
      gender: voiceInfo.gender,
      name: voiceInfo.voiceName,
      username,
      locale: voiceInfo.region,
      provinceId: voiceInfo.province,
      avatar: voiceInfo.avatar,
      type: VOICE_CLONE_TYPE.ZERO_SHOT,
      instantVoiceId: audioDraft?.voiceId,
      ageGroup: voiceInfo?.ageGroup,
    };
    try {
      const response = await apis.voiceCloning.createVoiceCloningVoice(data);
      if (!response?.status) {
        if (response.errorCode === USERNAME_EXISTS) {
          setError({ ...error, voiceName: t('voiceNameExistsError') });
          return;
        }
        if (response.errorCode === ERROR_CODE.VOICE_USERNAME_INVALID) {
          setError({ ...error, voiceName: t('voiceNameDescription') });
          return;
        }
        dispatch(
          actions.noti.push({
            severity: 'error',
            message: 'errorOccurred',
          }),
        );
        return;
      }

      dispatch(
        actions.noti.push({
          severity: 'success',
          message: 'createVoiceSuccess',
        }),
      );
      await refreshVoices();
      closePopupInstantVoiceCloning();
      dispatch(actions.voiceVoiceCloning.closePopupVoiceCloningOptions());
    } catch (error) {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: 'errorOccurred',
        }),
      );
    } finally {
      setIsLoadingCreateVoice(false);
    }
  };

  const handleTryAgain = async () => {
    setIsLoadingTryAgain(true);
    const { voiceId, ...restAudioDraft } = audioDraft;
    try {
      const response =
        await apis.instantVoiceTraining.createInstantVoiceTraining(
          restAudioDraft,
        );
      if (response?.status) {
        setAudioDraft({
          ...restAudioDraft,
          voiceId: response?.result?.voiceId,
        });
        setVoiceProcessingStatus(VOICE_PROCESSING_STATUS.PROCESSING);
        setProgress(0);
      }
    } catch (error) {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: 'errorOccurred',
        }),
      );
    } finally {
      setIsLoadingTryAgain(false);
    }
  };

  return (
    <StyledCreateVoice>
      {voiceProcessingStatus === VOICE_PROCESSING_STATUS.PROCESSING && (
        <VoiceProcessing progress={Math.floor(progress)} />
      )}
      {voiceProcessingStatus === VOICE_PROCESSING_STATUS.FAILURE && (
        <VoiceProcessingError />
      )}
      {voiceProcessingStatus === VOICE_PROCESSING_STATUS.SUCCESS && (
        <>
          <AvatarInput avatar={avatar} setAvatar={setAvatar} />
          <AudioPlay
            originalAudioLink={audio.originalAudioLink}
            cloneAudioLink={audio.cloneAudioLink}
          />
        </>
      )}

      <Grid container spacing={2} mb={3} mt={0}>
        <Grid item xs={12} sm={6}>
          <LimitedInput
            label={t('voiceName')}
            name="voiceName"
            value={voiceInfo.voiceName}
            placeholder={t('enterVoiceName')}
            disabled={isLoadingCreateVoice}
            onChange={handleSelect}
            error={error.voiceName}
            helperText={error.voiceName}
            styleTextField={{
              '& .MuiFormHelperText-root.Mui-error': {
                marginTop: '0px',
                fontSize: '14px',
                fontWeight: '400',
                lineHeight: '20px',
                color: COLOR.redPrimary,
              },
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Select
            label={t('contentType')}
            name="contentType"
            value={voiceInfo.contentType}
            placeholder={t('selectContentType')}
            disabled={isLoadingCreateVoice}
            onChange={handleSelect}
            options={categories}
            fullWidth
            getOptionLabel={(option) =>
              getCategoryLabel(option.name, i18n.language)
            }
            getOptionValue={(option) => option.id}
            error={error.contentType}
            helperText={error.contentType}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Select
            label={t('gender')}
            name="gender"
            value={voiceInfo.gender}
            placeholder={t('selectGender')}
            disabled={isLoadingCreateVoice}
            onChange={handleSelect}
            options={GENDER}
            fullWidth
            getOptionLabel={(option) => t(option.label)}
            getOptionValue={(option) => option.value}
            error={error.gender}
            helperText={error.gender}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Select
            label={t('ageGroup')}
            name="ageGroup"
            value={voiceInfo.ageGroup}
            placeholder={t('placeHolderGroupAge')}
            disabled={isLoadingCreateVoice}
            onChange={handleSelect}
            options={ageGroupOptions}
            fullWidth
            getOptionLabel={(option) => t(option.label)}
            getOptionValue={(option) => option.value}
            error={error.ageGroup}
            helperText={error.ageGroup}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Select
            label={t('region')}
            name="region"
            value={voiceInfo.region}
            placeholder={t('selectRegion')}
            disabled={isLoadingCreateVoice}
            onChange={handleSelect}
            options={Object.values(VOICE_LOCALES)}
            fullWidth
            getOptionLabel={(option) => t(option)}
            getOptionValue={(option) => option}
            error={error.region}
            helperText={error.region}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Select
            label={t('voiceProvince')}
            name="province"
            value={voiceInfo.province}
            placeholder={t('selectProvince')}
            disabled={isLoadingCreateVoice}
            onChange={handleSelect}
            options={provinces}
            fullWidth
            getOptionLabel={(option) => t(option.name)}
            getOptionValue={(option) => option.id}
            error={error.province}
            helperText={error.province}
          />
        </Grid>
      </Grid>

      <FormControlLabel
        sx={{ textAlign: 'left', marginRight: '0px' }}
        control={
          <Checkbox
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            disabled={isLoadingCreateVoice}
          />
        }
        label={
          <Typography
            variant="body2"
            component="span"
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(t('confirmVoiceClone'), {
                allowedTags: ['a', 'strong', 'em', 'br'],
                allowedAttributes: {
                  a: ['href', 'target', 'rel', 'style'],
                },
              }),
            }}
          />
        }
      />

      {voiceProcessingStatus === VOICE_PROCESSING_STATUS.FAILURE ? (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '16px' }}>
          <CommonButton
            size="small"
            variant="outlined"
            onClick={() => {
              onResetMetadataAudio();
              onSetStep(STEPS.UPLOAD_AUDIO);
            }}
            textProps={{
              variantText: 'body-14-bold',
              color: COLOR.darkBlue,
            }}
          >
            {t('createNewVoice')}
          </CommonButton>
          <CommonButton
            size="small"
            onClick={handleTryAgain}
            variant="contained"
            disabled={isLoadingTryAgain}
            bgrDisabled={COLOR.yellow[32]}
            textProps={{
              variantText: 'body-14-bold',
              color: COLOR.darkBlue,
            }}
            startIcon={
              isLoadingTryAgain ? (
                <CircularProgress size={24} className="btn-loading" />
              ) : (
                <></>
              )
            }
          >
            {t('tryAgain')}
          </CommonButton>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <CommonButton
            size="small"
            onClick={handleCreateVoice}
            variant="contained"
            disabled={disabledCreateVoice}
            bgrDisabled={COLOR.yellow[32]}
            textProps={{
              variantText: 'body-14-bold',
              color: COLOR.darkBlue,
            }}
            startIcon={
              isLoadingCreateVoice ? (
                <CircularProgress size={24} className="btn-loading" />
              ) : (
                <></>
              )
            }
          >
            {t('createVoice')}
          </CommonButton>
        </Box>
      )}
    </StyledCreateVoice>
  );
};

export default CreateVoice;
